import { localDateOf } from '../../engine';
import { apiFetch } from './http';

// ─────────────────────────────────────────────────────────────────────
// THE PERSON'S OWN DATA — export and erasure (#1062, the client half).
//
//   GET  /me/data-export   a JSON attachment of what StarPoint holds about the
//                          person (PERSONAL_DATA.md § 3.1), `no-store`.
//   POST /me/erase         closes the account and erases StarPoint's own records
//                          in ONE transaction (§ 5.2). Irreversible.
//
// Both take the account from the session; nothing is sent. Both want a sign-in
// from the last fifteen minutes: an older one is answered 403
// `reauthentication_required`, which the TRANSPORT reports so the step-up
// prompt opens (`lib/api/http.ts`); the caller shows the code and the person
// presses again — the same as «Avslutt kontoen».
//
// Answers are CODES; the screen decides the sentence.
// ─────────────────────────────────────────────────────────────────────

export interface PersonalDataOutcome {
  /** A machine code from the API, or null on success. Never a sentence. */
  code: string | null;
}

async function codeOf(res: Response): Promise<string> {
  const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
  return typeof body?.error === 'string' ? body.error : `http_${res.status}`;
}

/**
 * The file name, built here in the server's shape: `starpoint-data-<day>.json`.
 * Not read from the response: cross-origin a browser hides `Content-Disposition`
 * unless the API exposes it, which it does not (found in the browser walk —
 * every download came out under a bare default), and reading a header the API
 * does not expose is exactly what `cors-contract.test.ts` refuses. The day is
 * the LOCAL one (DOMAIN_RULES §4: never `toISOString()`); it is a file name,
 * never a key.
 */
export function exportFileName(now: number = Date.now()): string {
  return `starpoint-data-${localDateOf(now)}.json`;
}

/** How the browser is asked to save a file. Injectable for tests. */
export type SaveFile = (blob: Blob, fileName: string) => void;

/**
 * Save through a temporary object URL and a click on a detached anchor — the
 * one way that works for a response read with `fetch` (the request needs the
 * session's credentials, so a plain link to the API cannot be used).
 */
export const saveWithAnchor: SaveFile = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // After the click has handed the blob to the download; nothing keeps it.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
};

/** «Last ned mine data»: fetch the export and hand it to the browser as a file. */
export async function downloadMyData(
  save: SaveFile = saveWithAnchor,
): Promise<PersonalDataOutcome> {
  let res: Response;
  try {
    res = await apiFetch('/me/data-export', { method: 'GET', cache: 'no-store' });
  } catch {
    return { code: 'unreachable' };
  }
  if (!res.ok) return { code: await codeOf(res) };
  let blob: Blob;
  try {
    blob = await res.blob();
  } catch {
    return { code: 'unreachable' };
  }
  save(blob, exportFileName());
  return { code: null };
}

/**
 * «Slett kontoen min og dataene mine». On success every session the account
 * held is gone, this one included — the caller leaves the signed-in surface.
 */
export async function eraseMyAccount(): Promise<PersonalDataOutcome> {
  let res: Response;
  try {
    res = await apiFetch('/me/erase', { method: 'POST' });
  } catch {
    return { code: 'unreachable' };
  }
  if (res.ok) return { code: null };
  return { code: await codeOf(res) };
}
